import { ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { cloneDeep } from 'lodash';
import { FacebookFormConfiguration } from 'src/app/core/models/social-forms-configurations/facebook-form-configuration.model';
import { LobbyErrorHandlerService } from 'src/app/core/services/lobby-error-handler.service';
import { SocialFormsFacebookService } from 'src/app/features/lead-injector/services/social-forms-facebook.service';
import { AutocompleteLeadInjectorIdSuggestionService } from 'src/app/shared/common-features/services/autocomplete-strategies/autocomplete-lead-injector-id-suggestion.service';
import { LeadInjectorService } from 'src/app/features/lead-injector/services/lead-injector.service';
import { finalize, iif, of, switchMap, tap } from 'rxjs';
import { LeadInjectorDashBoardAction } from 'src/app/features/lead-injector/lead-injector.actions';
import { ScreenSizeAwareComponent } from '../../../common-bases/screen-size-aware-component';
import { ConfirmDialogComponent } from '../../../dialogs/confirm-dialog/confirm-dialog.component';
import { GlobalSpinnerActions } from '../../../global-spinner/actions-types';
import { SearchSuggestion } from '../../../inputs/autocomplete/search-strategy-base';
import { CloseSidenavAction } from '../../sidenav.actions';
import { LeadInjectorAction } from 'src/app/core/events/lead-injector.actions';

export interface FacebookFormConfigurationDetailsForm extends FormGroup {
  controls: {
    formId: FormControl<string | null>
    formName: FormControl<string | null>
    description: FormControl<string | null>

    injector: FormControl<SearchSuggestion | null>

    created: FormControl<Date | null>
    isEnabled: FormControl<boolean | null>
  }
}

@Component({
    selector: 'app-sidenav-content-facebook-form-info',
    templateUrl: './sidenav-content-facebook-form-info.component.html',
    styleUrls: ['./sidenav-content-facebook-form-info.component.scss'],
    standalone: false
})
export class SidenavContentFacebookFormInfoComponent extends ScreenSizeAwareComponent implements OnChanges {

  @Input() set formInfo(configuration: FacebookFormConfiguration | null) {
    if (configuration) {
      this.isCreatingNewForm = configuration.id == 0
      this._configuration = cloneDeep(configuration)
      this.configurationFirstState = cloneDeep(configuration)
      this.form = this.createFormGroup(configuration!)
      this.subscribeToDetailsForm(this.form)
    } else {
      this._configuration = undefined
      this.configurationFirstState = undefined
      this.form = undefined
    }
    this.cdr.markForCheck()
  }

  @Input() readOnly = false
  @Input() canDelete = true

  @Output() close = new EventEmitter()

  form: FacebookFormConfigurationDetailsForm | undefined
  configurationFirstState: FacebookFormConfiguration | undefined

  isCreatingNewForm: boolean = false

  private _configuration: FacebookFormConfiguration | undefined

  constructor(
    private readonly appStore: Store,
    private readonly socialFormsFacebookService: SocialFormsFacebookService,
    private readonly dialog: MatDialog,
    private readonly cdr: ChangeDetectorRef,
    public readonly injectorIdSuggestionService: AutocompleteLeadInjectorIdSuggestionService,
    private readonly leadInjectorService: LeadInjectorService,
    private readonly errorHandlerService: LobbyErrorHandlerService) {
    super()
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.readOnly) this.form?.disable()
    this.cdr.markForCheck()
  }

  onSelectedInjectorSuggestion(suggestion: SearchSuggestion | null) {
    if (suggestion)
      this.leadInjectorService.getLeadInjector$(Number(suggestion?.id!)).pipe(
        tap(() => this.appStore.dispatch(GlobalSpinnerActions.showGlobalSpinner())),
        tap(injector => this._configuration!.injector = injector),
        tap(() => this.appStore.dispatch(GlobalSpinnerActions.hideGlobalSpinner()))
      ).subscribe()
  }

  onCloseClick() {
    this.confirmClose()
  }

  onCancelClick() {
    this.confirmClose()
  }

  onSaveClick() {
    of(void 0).pipe(
      tap(() => this.appStore.dispatch(GlobalSpinnerActions.showGlobalSpinner())),
      switchMap(() => iif(() => this.isCreatingNewForm,
        this.socialFormsFacebookService.createFacebookForm$(this._configuration!)
          .pipe(this.errorHandlerService.handleError),
        this.socialFormsFacebookService.updateFacebookForm$(this._configuration!.id, this._configuration!)
          .pipe(this.errorHandlerService.handleError)
      )),
      finalize(() => this.appStore.dispatch(GlobalSpinnerActions.hideGlobalSpinner())),
      tap(() => this.close.emit()
      )).subscribe()
  }

  onDeleteClick() {
    const confirmDialog = this.dialog.open(ConfirmDialogComponent, ConfirmDialogComponent.createConfigurations(
      {
        title: 'Elimina',
        message: 'Questa azione è irreversibile',
        confirmButtonText: 'Procedi',
        cancelButtonText: 'Annulla'
      }
    ))

    confirmDialog.afterClosed().subscribe(result => {
      if (result)
        of(void 0).pipe(
          tap(() => this.appStore.dispatch(GlobalSpinnerActions.showGlobalSpinner())),
          switchMap(() => this.socialFormsFacebookService.deleteFacebookForm$(this._configuration!.id!)),
          this.errorHandlerService.handleError,
          finalize(() => this.appStore.dispatch(GlobalSpinnerActions.hideGlobalSpinner())),
          tap(() => this.appStore.dispatch(CloseSidenavAction())),
        ).subscribe()
    })
  }

  onLeadInjectorInfoClick() {
    this.appStore.dispatch(LeadInjectorDashBoardAction.leadInjectorInfoView(this._configuration!.injector!.id!))
  }

  private confirmClose() {
    if (this.form!.dirty) {
      const confirmDialog = this.dialog.open(ConfirmDialogComponent, ConfirmDialogComponent.createConfigurations(
        {
          title: 'Conferma',
          message: 'Modifiche non salvate. Se procedi saranno perdute',
          confirmButtonText: 'Procedi',
          cancelButtonText: 'Annulla'
        }
      ))

      confirmDialog.afterClosed().subscribe(result => {
        if (result) this.close.emit()
      })
    }
    else this.close.emit()
  }

  private createFormGroup(configuration: FacebookFormConfiguration): FacebookFormConfigurationDetailsForm {
    return new FormBuilder().group(
      {
        formId: new FormControl<string>({ value: configuration.formId, disabled: !this.isCreatingNewForm }),
        formName: new FormControl<string>(configuration.formName),

        description: new FormControl<string>(configuration.formDescription),

        injector: new FormControl<SearchSuggestion | null>(
          this._configuration?.injector ? { id: configuration.injector!.externalId!, value: configuration.injector!.description! } : null,
          [Validators.required]
        ),

        created: new FormControl<Date | null>(configuration.created),
        isEnabled: new FormControl<boolean>(configuration.isEnabled),
      }
    )
  }

  private subscribeToDetailsForm(form: FacebookFormConfigurationDetailsForm) {

    form.controls.formId.valueChanges
      .subscribe(value => this._configuration!.formId = value!)

    form.controls.description.valueChanges
      .subscribe(value => this._configuration!.formDescription = value!)

    form.controls.formName.valueChanges
      .subscribe(value => this._configuration!.formName = value!)

    form.controls.isEnabled.valueChanges
      .subscribe(value => this._configuration!.isEnabled = value!)

  }
}
